import React from 'react'

import Reflux from 'reflux-react-16'
import _ from 'lodash'
import moment from 'moment'

import UserStore from '../../stores/UserStore.js'
import UserActions from '../../actions/UserActions.js'

import UserModal from '../../components/users/UserModal.js'
import {Button, Colors, IconButton, Panel, Popup} from '../../components/UI/index.js'
import Table from '../../components/UI/table/index.js'
import SearchBar from '../../components/SearchBar/index.js'
import csvExport from '../../utils/csvExport.js'
import filter from '../../utils/filter.js'
import ImportUserModal from '../../components/users/ImportUserModal.js'
import LoginActions from '../../actions/LoginActions.js'
import t from 'src/helpers/Languages.js'
import {getAvailableBranches} from '../../utils/branches.js'
import branchesAvailable from '@utils/branchesAvailable.js'
import {formSpacing} from '../../components/UI/forms/index.js'
import createMemo from '../../utils/createMemo.js'

class Users extends Reflux.Component {
    constructor(props) {
        super(props)

        this.columnsMemo = createMemo()

        this.store = UserStore
    }

    componentDidMount() {
        const {reseller} = this.props
        document.title = `Gebruikers • ${reseller.settings.accountName || reseller.name}`
    }

    // The same columns until a value they read changes: new columns re-render every row of the table
    columns() {
        return this.columnsMemo([this.props.reseller], () => this.buildColumns())
    }

    buildColumns() {
        const {reseller} = this.props

        return [
            ...branchesAvailable(reseller) ?
                [{id: 'branchNames', title: t('branches'), visible: false, key: 'branchNames', width: 180, text: (user) => {
                    return getAvailableBranches(reseller, user).map((branch) => branch.name).join(', ')
                }}] :
                [],
            {id: 'name', title: 'Naam', visible: true, key: 'name', flex: 1},
            {id: 'phone', title: 'Telefoon', visible: true, key: 'phone', flex: 1},
            {id: 'email', title: 'Emailadres', visible: true, key: 'email', flex: 1},
            {id: 'lastActive', title: 'Laatst actief', visible: true, key: 'lastActive', flex: 1, text: (user) => {
                return user.lastActive ? moment(user.lastActive).format('DD-MM-YYYY HH:mm') : ''
            }, sort: (user) => user.lastActive},
            {id: 'edit', visible: true, showOnHover: true, width: 50, render: (user) => {
                return (
                    <IconButton onClick={this.onClickEdit.bind(this, user)}>
                        <i className='mdi mdi-pencil' />
                    </IconButton>
                )
            }}
        ]
    }

    onChangeQueries(userQueries) {
        this.table.selectPage(1)
        UserActions.setQueries(userQueries)
    }

    onChangeTable(selectedUsers) {
        UserActions.setSelected(selectedUsers)
    }

    onClickNew() {
        this.modal.open()
        UserActions.setSelected([])
    }

    onClickImport() {
        this.importModal.open()
        UserActions.setSelected([])
    }

    onClickEdit(user) {
        this.modal.open(user)
    }

    onClickRemove() {
        const {selectedUsers} = this.state

        this.popup.open('Gebruikers verwijderen', 'Gebruikers worden definitief verwijderd.', () => {
            UserActions.setSelected([])

            UserActions.remove(selectedUsers, (err) => {
                if (err) {
                    this.popup.setError(err)
                } else {
                    this.popup.close()
                }
            })
        })
    }

    async onClickAssignBranches() {
        const {selectedUsers, users} = this.state
        const {reseller} = this.props

        const availableBranches = getAvailableBranches(reseller)

        this.popup.setInputs([
            {label: t('branches'), name: 'branchIds', values: [], type: 'select', options: availableBranches.map((branch) => ({value: branch.id, title: branch.name}))},
            {label: t('action'), name: 'mode', value: 'add', type: 'select', options: [
                {value: 'add', title: t('add')},
                {value: 'replace', title: t('replace')},
                {value: 'remove', title: t('delete')}
            ]}
        ])

        const values = await this.popup.open(t('assignBranches'), t('chooseBranchesAndActionForUsers'))
        if (!values) return

        UserActions.setSelected([])

        const branchIds = values.branchIds || []
        const mode = values.mode || 'add'

        if (branchIds.length === 0) {
            this.popup.setError(t('selectAtLeastOneBranch'))
            return
        }

        let success = 0
        const errors = []
        this.popup.setProgress(0, selectedUsers.length)

        for (const id of selectedUsers) {
            const user = _.find(users, (u) => u._id === id)

            if (!user) {
                continue
            }

            let newBranchIds = user.branchIds || []

            if (mode === 'add') {
                newBranchIds = _.union(newBranchIds, branchIds)
            } else if (mode === 'replace') {
                newBranchIds = branchIds
            } else if (mode === 'remove') {
                newBranchIds = newBranchIds.filter((branchId) => !branchIds.includes(branchId))
            }

            await new Promise((resolve) => {
                UserActions.edit({...user, branchIds: newBranchIds}, (err) => {
                    if (err) {
                        errors.push(err)
                    } else {
                        success += 1
                    }

                    this.popup.setProgress(success + errors.length, selectedUsers.length)
                    resolve()
                })
            })
        }

        this.popup.setMessage(`${success} ${success === 1 ? 'gebruiker' : 'gebruikers'} bewerkt.`)
        this.popup.setErrors(errors)
    }

    async onClickExport() {
        const {users, selectedUsers} = this.state
        const exportUsers = []

        selectedUsers.map((id) => {
            const user = _.find(users, (u) => u._id === id)
            if (user) {
                exportUsers.push(user)
            }
        })

        await this.popup.openProgress(t('export'), '', (onProgress) => csvExport.users(exportUsers, onProgress))

        UserActions.setSelected([])
    }

    onClickSendWelcomeEmail() {
        const {selectedUsers, users} = this.state

        const usersToSend = []

        selectedUsers.map((id) => {
            const user = users.find((u) => u._id === id)

            if (user.email) {
                usersToSend.push(user)
            }
        })

        this.popup.open('Verstuur welkomstmail', `Weet je zeker dat je ${usersToSend.length} welkomstmail${usersToSend.length === 1 ? '' : 's'} wilt versturen?`, async () => {
            const errors = []

            for (const user of usersToSend) {
                await new Promise((resolve) => {
                    LoginActions.sendUserWelcomeMail(user, (err) => {
                        if (err) {
                            errors.push(err)
                        }

                        resolve()
                    })
                })
            }

            if (errors.length > 0) {
                this.popup.setError(errors)
            } else {
                this.popup.setMessage('Welkomstmails zijn verstuurd.')
            }
        })
    }

    render() {
        const {selectedUsers, usersLoading, userQueries} = this.state
        const {reseller, user} = this.props

        const users = filter.users(this.state.users, userQueries, reseller)
        const availableBranches = getAvailableBranches(reseller)

        return (
            <div style={{display: 'flex', flexDirection: 'column', height: '100%'}}>
                <Panel style={{display: 'flex', columnGap: formSpacing.columnGap, borderTop: 'none', borderLeft: 'none', borderRight: 'none', background: Colors.backgroundNeutral, padding: '20px 10px 10px 10px'}}>

                    <SearchBar
                        columns={this.columns()}
                        onQueryChange={this.onChangeQueries.bind(this)}
                        queries={userQueries}
                    />

                    <Button
                        variant='outline-white'
                        onClick={this.onClickNew.bind(this)}
                    >
                        Nieuwe Gebruiker
                    </Button>

                    <Button
                        variant='outline-white'
                        onClick={this.onClickImport.bind(this)}
                    >
                        <i className='mdi mdi-file-import' />
                    </Button>

                    {selectedUsers.length > 0 &&
                        <>
                            <Button
                                variant='outline-white'
                                onClick={this.onClickExport.bind(this)}
                            >
                                <i className='mdi mdi-file-export' />
                            </Button>

                            <Button
                                variant='outline-white'
                                onClick={this.onClickRemove.bind(this)}
                            >
                                <i className='mdi mdi-delete' />
                            </Button>

                            {availableBranches.length > 0 &&
                                <Button
                                    variant='outline-white'
                                    onClick={this.onClickAssignBranches.bind(this)}
                                    tooltip={t('assignBranches')}
                                >
                                    <i className='mdi mdi-office-building-marker-outline' />
                                </Button>
                            }

                            <Button
                                variant='outline-white'
                                onClick={this.onClickSendWelcomeEmail.bind(this)}
                            >
                                Verstuur welkomstmail
                            </Button>
                        </>
                    }
                </Panel>

                <div style={{flex: 1, marginTop: 24, marginRight: 24, marginLeft: 24}}>
                    <Table
                        tableName='users'
                        columns={this.columns()}
                        rows={users}
                        selectedRows={selectedUsers}
                        loading={usersLoading}
                        onChange={this.onChangeTable.bind(this)}
                        ref={(ref) => this.table = ref}
                    />
                </div>

                <UserModal reseller={reseller} activeUser={user} ref={(ref) => this.modal = ref} />

                <ImportUserModal
                    ref={(modal) => this.importModal = modal}
                />
                <Popup ref={(ref) => this.popup = ref} />
            </div>
        )
    }
}

export default (Users)
